/*
 * Ecranul 4b: detaliul unei cereri. Cel mai important din aplicatie.
 *
 * Articolele stau primele, apoi jurnalul. Atat: cine intra aici e omul care a
 * cerut ceva si vrea sa stie ce se intampla cu ce a cerut.
 *
 * Taskurile NU se mai arata. Erau primele pe pagina, cu tot cu tabla lor de
 * anulat si deblocat pasi. Aia e munca echipelor de indeplinire - ce are de
 * facut fiecare si in ce ordine - iar cine a cerut un ATM n-are ce hotari
 * acolo si nici cum sa inteleaga un pas blocat. Ecranul lui raspunde la "unde e
 * cererea mea", si la aia raspund starile articolelor si tichetele lor.
 *
 * Deci nici ruta de taskuri nu se mai cere. Ramane in api si in tipuri, pentru
 * portal; aici nu se mai atinge, si ecranul incarca o singura data in loc de
 * doua.
 *
 * Ecranul asta si lista de cereri sunt de acum SINGURUL loc din magazin unde se
 * vede o aprobare. Ecranul de aprobari a disparut: aprobarea e o mutare de
 * stare pe tichet. De-asta fiecare articol isi poarta aici starea tichetului si
 * legatura catre el, iar ecranul spune cu litere unde s-a mutat decizia - altfel
 * omul care vedea pana acum "asteapta aprobare" in magazin crede ca cererea lui
 * s-a pierdut.
 */

import { Link, useParams } from 'react-router-dom';
import * as shop from '../api/shop';
import type { RequestDetail, RequestItem } from '../api/types';
import { Badge } from '../components/Badge';
import { ErrorState, Loading } from '../components/states';
import { EM_DASH, formatDateTime, formatMoney, personName } from '../lib/format';
import { ticketOfItem, ticketSubject } from '../lib/ticket';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';
import { ExternalLink } from '../components/ExternalLink';

/**
 * Celula de tichet a unui articol.
 *
 * Ordinea in care se cade inapoi spune ce se stie:
 *   1. starea tichetului - adevarul de acum, acolo se aproba;
 *   2. starea articolului - ce trimitea api-ul inainte de mutare, pastrata ca
 *      sa nu ramana coloana goala pe un api care inca n-a fost actualizat;
 *   3. liniuta - chiar nu se stie nimic.
 *
 * Legatura se deschide in fila noua: tichetul e in alta aplicatie, iar cine
 * verifica o cerere cu mai multe articole se intoarce la ea.
 */
function ItemTicket({ item }: { item: RequestItem }) {
  const ticket = ticketOfItem(item);
  const itemState = item.state ?? item.status;

  if (!ticket) return itemState ? <Badge state={itemState} /> : <>{EM_DASH}</>;

  return (
    <>
      {ticket.state ? (
        <Badge state={ticket.state} label={ticket.label ?? undefined} />
      ) : itemState ? (
        <Badge state={itemState} />
      ) : null}

      {ticket.id !== null ? (
        <span className="table__sub">
          {ticket.href ? (
            <ExternalLink href={ticket.href}>{t('Ticket')}<span className="num">#{ticket.id}</span>
            </ExternalLink>
          ) : (
            <>{t('Ticket')}<span className="num">#{ticket.id}</span>
            </>
          )}
        </span>
      ) : null}

      {/*
          Cine tine tichetul acum, si de cand.

          Sub numarul tichetului, marunt: raspunsul principal ramane starea, iar
          asta e ce intreaba omul imediat dupa - cine se ocupa, si mai am de
          asteptat? Numele omului cand exista, altfel grupul: un tichet ajuns la
          o echipa dar neluat inca de nimeni e o stare adevarata, nu o lipsa.
      */}
      {ticket.assignee || ticket.group || ticket.updated ? (
        <span className="table__sub table__muted">
          {ticket.assignee ?? ticket.group ?? t('nobody yet')}
          {ticket.assignee && ticket.group ? ` · ${ticket.group}` : ''}
          {ticket.updated ? ` · ${formatDateTime(ticket.updated)}` : ''}
        </span>
      ) : null}
    </>
  );
}

/**
 * Tichetele deschise sub cel al articolului.
 *
 * Un serviciu poate depinde de altele, si atunci cererea deschide cate un
 * tichet si pentru acelea. Nu sunt articole - nimeni nu le-a pus in cos - deci
 * n-au rand propriu in tabel; stau sub numele serviciului care le-a adus,
 * fiindca aia e si relatia dintre ele.
 *
 * Nu se deseneaza nimic cand nu sunt. Un serviciu fara dependente n-are de ce
 * sa capete un rand gol care sa-i spuna ca n-are.
 */
function ItemChildren({ item }: { item: RequestItem }) {
  const children = item.children ?? [];

  if (children.length === 0) return null;

  return (
    <ul className="subtickets">
      {children.map((child) => (
        <li className="subtickets__item" key={child.id}>
          <span className="subtickets__num num">#{child.id}</span>
          <span className="subtickets__subject">{ticketSubject(child.subject) || t('Ticket')}</span>
          {child.status?.name ? <Badge state={child.status.name} /> : null}
        </li>
      ))}
    </ul>
  );
}

export function RequestDetailScreen() {
  const { id = '' } = useParams();

  const bundle = useAsync<RequestDetail>(
    async (signal) => {
      const detailResponse = await shop.requestDetail(id, { signal });

      /* Plic cu cheie, ca la fisa serviciului: api-ul intoarce {"request": {...}}. */
      return detailResponse.request;
    },
    [id],
  );

  if (bundle.status === 'loading') return <Loading label={t('Loading request…')} />;
  if (bundle.status === 'error' && bundle.error) {
    return <ErrorState error={bundle.error} onRetry={bundle.reload} />;
  }
  if (!bundle.data) return null;

  const detail = bundle.data;
  /* Contractul nu spune daca cererea vine la radacina sau sub `request`. */
  const request = detail.request ?? detail;
  const items = detail.items ?? request.items ?? [];
  /* `events` e numele din api - vezi RequestsController::showAction(). Celelalte
     trei raman ca variante: contractul nu fixeaza numele jurnalului, si o
     instalare mai veche il poate trimite altfel. */
  const log = detail.events ?? detail.log ?? detail.journal ?? detail.history ?? [];
  const requester = personName(detail.requester ?? detail.user);

  /*
   * Starea din capul paginii: cea a tichetului, cand se poate.
   *
   * `state` al cererii ramane "submitted" pentru totdeauna dupa trimitere -
   * magazinul si-a facut treaba si nu mai are ce spune. Ce se intampla cu
   * cererea se intampla pe tichet, de cand aprobarea s-a mutat acolo, deci
   * eticheta care sta cel mai la vedere trebuie sa arate chiar starea aia.
   *
   * Numai cand cererea are un singur articol. Cu doua articole sunt doua
   * tichete cu stari diferite, si o singura eticheta ar trebui sa aleaga unul
   * dintre ele - adica sa ascunda celalalt. Atunci ramane starea cererii, si
   * starile adevarate se citesc pe randuri, in coloana Ticket.
   */
  const lone = items.length === 1 ? ticketOfItem(items[0]!) : null;
  const headState = lone?.state ?? request.state ?? request.status;
  const headLabel = lone?.state ? (lone.label ?? undefined) : undefined;

  return (
    <>
      <p className="backlink">
        <Link to="/requests">{t('Back to my requests')}</Link>
      </p>

      <header className="page-head">
        <div>
          <p className="micro-label">{t('Request')}</p>
          <h1 className="page-title num">#{request.id ?? id}</h1>
          <p className="page-sub">
            Submitted {formatDateTime(request.created_at ?? request.submitted_at)}
            {requester ? ` by ${requester}` : ''}
          </p>
        </div>
        <Badge state={headState} label={headLabel} />
      </header>

      <section className="panel panel--flush panel--lead">
        <div className="panel__head">
          <h2 className="panel__title">{t('Items')}</h2>
        </div>
        {items.length === 0 ? (
          <p className="panel__body muted">{t('This request has no items.')}</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('Service')}</th>
                  <th scope="col">{t('Note')}</th>
                  <th scope="col" className="num-col">{t('Qty')}</th>
                  <th scope="col" className="num-col">{t('Cost')}</th>
                  <th scope="col">{t('Ticket')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="table__strong">
                      {item.name ?? item.catalog?.name ?? `Item #${item.id}`}
                      <ItemChildren item={item} />
                    </td>
                    <td className="table__muted">{item.note || EM_DASH}</td>
                    <td className="num-col num">{item.quantity ?? 1}</td>
                    <td className="num-col num">{formatMoney(item.cost)}</td>
                    <td>
                      <ItemTicket item={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/*
       * Jurnalul e ascuns deocamdata.
       *
       * Ascuns, nu sters: sectiunea era intreaga si corecta, iar ce s-a
       * intamplat cu o cerere ramane o intrebare buna - doar ca nu acum. Cand
       * se intoarce, se sterge `false &&` si atat; asa nu trebuie scrisa a doua
       * oara, si nici nu ramane cod mort fara sa se vada ca e oprit dinadins.
       */}
      {false && (
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">{t('History')}</h2>
          </div>
          {log.length === 0 ? (
            <p className="muted">{t('No history entries yet.')}</p>
          ) : (
            <ol className="log">
              {log.map((entry, index) => (
                // Jurnalul n-are id in contract; indexul e singura cheie stabila
                // disponibila, iar lista nu se reordoneaza si nu se filtreaza.
                <li className="log__item" key={index}>
                  <span className="log__when num">
                    {formatDateTime(entry.created_at ?? entry.at ?? entry.date)}
                  </span>
                  <span className="log__body">
                    <span className="log__who">
                      {personName(entry.user) || entry.author || t('System')}
                    </span>
                    <span className="log__what">
                      {entry.message ?? entry.text ?? entry.action ?? ''}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </>
  );
}
